/** Descriptors are clipped before normalising: bank descriptors are short and this bounds every regex below. */
export const MAX_DESCRIPTOR_LENGTH = 256;
const MONTHS = 'JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|SEPT|OCT|NOV|DEC';
/** Leading bank/card boilerplate, longest first. */
const PREFIXES = [
    'VISA DEBIT PURCHASE', 'MASTERCARD PURCHASE', 'DEBIT CARD PURCHASE', 'INTERNATIONAL PURCHASE', 'ONLINE PURCHASE',
    'EFTPOS PURCHASE', 'VISA PURCHASE', 'CARD PURCHASE', 'POS PURCHASE', 'TAP AND PAY', 'DIRECT DEBIT', 'DIRECT CREDIT',
    'OSKO PAYMENT', 'VISA DEBIT', 'CONTACTLESS', 'RECURRING', 'PURCHASE', 'EFTPOS', 'DEBIT', 'BPAY', 'POS', 'DD',
];
/** Payment processors that prefix the real merchant ("PAYPAL *NETFLIX"). */
const PROCESSOR = /^(?:PAYPAL|PP|SQ|SP|TST|SUMUP|ZLR|IZ|LS|GOOGLE|APPLE\.COM\/BILL)\s*\*\s*/;
const COUNTRIES = new Set(['AU', 'AUS', 'NZ', 'NZL', 'GB', 'GBR', 'UK', 'US', 'USA', 'IE', 'IRL', 'CA', 'CAN', 'SG', 'SGP']);
const STATES = new Set(['NSW', 'NS', 'VIC', 'QLD', 'QL', 'WA', 'SA', 'TAS', 'ACT', 'NT']);
const CITIES = new Set([
    'SYDNEY', 'MELBOURNE', 'BRISBANE', 'PERTH', 'ADELAIDE', 'HOBART', 'DARWIN', 'CANBERRA', 'GOLD COAST', 'NEWCASTLE',
    'LONDON', 'MANCHESTER', 'AUCKLAND', 'WELLINGTON', 'NEW YORK', 'SAN FRANCISCO', 'DUBLIN', 'SINGAPORE',
]);
const SUFFIXES = new Set(['PTY', 'LTD', 'LIMITED', 'INC', 'LLC', 'PL', 'P/L', 'CO']);
const titleCase = (s) => s.toLowerCase().replace(/(^|[\s\-/(])([a-z])/g, (_m, p, c) => p + c.toUpperCase());
/**
 * Clean a raw bank descriptor into a merchant name: strips card numbers, dates, times, store numbers, card/EFTPOS
 * boilerplate, processor prefixes (PAYPAL *), reference codes and trailing locations, then title-cases.
 * ponytail: heuristic, no merchant database; a token after a store number or before a state code is treated as location.
 */
export const normaliseDescriptor = (input) => {
    const raw = input.slice(0, MAX_DESCRIPTOR_LENGTH);
    let s = ` ${raw.toUpperCase().replace(/\s+/g, ' ')} `;
    s = s
        .replace(/\b(?:\d{4}[ -]?){3}\d{4}\b/g, ' ') // full card numbers
        .replace(/\b\d{4,6}[X*]{4,}\d{2,4}\b/g, ' ') // 436712******1234
        .replace(/(?:^|\s)[X*]{2,}[ -]?\d{2,4}\b/g, ' ') // XXXX1234 / ****1234
        .replace(/\bCARD\s*(?:NO\.?|NUMBER)?\s*[X*\d]{4,}\b/g, ' ')
        .replace(/\b\d{4}-\d{2}-\d{2}\b/g, ' ') // 2025-10-15
        .replace(/\b\d{1,2}[/.-]\d{1,2}(?:[/.-]\d{2,4})?\b/g, ' ') // 15/10, 15/10/25
        .replace(new RegExp(`\\b\\d{1,2}\\s?(?:${MONTHS})[A-Z]*(?:\\s?\\d{2,4})?\\b`, 'g'), ' ') // 15OCT, 15 OCT 2025
        .replace(/\b\d{1,2}:\d{2}(?::\d{2})?\s?(?:AM|PM)?\b/g, ' ') // times
        // label + following token, dropped only when the token has a digit; whole token consumed either way (linear).
        .replace(/\b(?:VALUE DATE|REF(?:ERENCE)?|RECEIPT|RCPT|INV(?:OICE)?|TXN|TRANS(?:ACTION)?|AUTH|ID|NO)\b\.?:?\s*#?([A-Z0-9-]+)/g, (m, tok) => (/\d/.test(tok) ? ' ' : m))
        .replace(/#\s?\d+/g, ' ')
        .replace(/\bSTORE\s?\d+\b/g, ' ')
        .replace(/\b[A-Z0-9]{6,}\b/g, (tok) => (tok.replace(/\D/g, '').length >= 2 && /[A-Z]/.test(tok) ? ' ' : tok)) // AB12CD34 refs
        .replace(/\s+/g, ' ')
        .trim();
    for (let changed = true; changed;) {
        changed = false;
        for (const p of PREFIXES) {
            if (s === p || s.startsWith(`${p} `)) {
                s = s.slice(p.length).trim();
                changed = true;
            }
        }
        const m = PROCESSOR.exec(s);
        if (m) {
            s = s.slice(m[0].length).trim();
            changed = true;
        }
        if (/^\d+\s/.test(s)) {
            s = s.replace(/^\d+\s/, '');
            changed = true;
        }
    }
    let tokens = s.replace(/[*_|~^`"<>{}[\]]/g, ' ').split(' ').map((t) => t.replace(/^[.,:;-]+|[.,:;-]+$/g, '')).filter(Boolean);
    const storeNo = tokens.findIndex((t, i) => i > 0 && /^\d+$/.test(t));
    if (storeNo > 0)
        tokens = tokens.slice(0, storeNo); // "WOOLWORTHS 1234 SYDNEY" → WOOLWORTHS
    tokens = tokens.filter((t) => !/^\d+$/.test(t));
    const trim = (pred) => {
        const last = tokens[tokens.length - 1];
        if (tokens.length > 1 && last !== undefined && pred(last)) {
            tokens.pop();
            return true;
        }
        return false;
    };
    while (trim((t) => COUNTRIES.has(t))) { /* strip */ }
    if (trim((t) => STATES.has(t)) && tokens.length > 2)
        tokens.pop(); // drop suburb before state
    for (let again = true; again;) {
        again = false;
        const lastTwo = tokens.slice(-2).join(' ');
        if (tokens.length > 2 && CITIES.has(lastTwo)) {
            tokens.splice(-2, 2);
            again = true;
        }
        else if (trim((t) => CITIES.has(t) || SUFFIXES.has(t)))
            again = true;
    }
    const out = tokens.join(' ').trim();
    return titleCase(out.length > 0 ? out : raw.trim().replace(/\s+/g, ' '));
};
/** Lower-case comparison key for a descriptor. */
export const descriptorKey = (raw) => normaliseDescriptor(raw).toLowerCase();
const tokensOf = (s) => s.toLowerCase().split(/[^a-z0-9&']+/).filter(Boolean);
const bigrams = (s) => {
    const t = s.toLowerCase().replace(/\s+/g, '');
    const out = [];
    for (let i = 0; i < t.length - 1; i++)
        out.push(t.slice(i, i + 2));
    return out;
};
/** Similarity 0..1: max of token Jaccard and character-bigram Dice. */
export const textSimilarity = (a, b) => {
    const ta = new Set(tokensOf(a));
    const tb = new Set(tokensOf(b));
    const inter = [...ta].filter((t) => tb.has(t)).length;
    const union = new Set([...ta, ...tb]).size;
    const jac = union === 0 ? 0 : inter / union;
    const ba = bigrams(a);
    const bb = bigrams(b);
    if (ba.length === 0 || bb.length === 0)
        return jac;
    const pool = new Map();
    for (const g of bb)
        pool.set(g, (pool.get(g) ?? 0) + 1);
    let hits = 0;
    for (const g of ba) {
        const n = pool.get(g) ?? 0;
        if (n > 0) {
            hits++;
            pool.set(g, n - 1);
        }
    }
    return Math.max(jac, (2 * hits) / (ba.length + bb.length));
};
const collapse = (s) => s.trim().replace(/\s+/g, ' ').toLowerCase();
/**
 * Best payee for a raw descriptor: exact alias (1.0) → normalised alias/name (0.95) → name prefix (0.9)
 * → fuzzy token/bigram similarity (≤ 0.8). Returns null below `minScore` (default 0.4).
 */
export const matchPayee = (raw, payees, minScore = 0.4) => {
    const exact = collapse(raw);
    const key = descriptorKey(raw);
    if (exact === '')
        return null;
    let best = null;
    for (const p of payees) {
        let score = 0;
        const names = [p.name, ...p.aliases];
        if (p.aliases.some((a) => collapse(a) === exact) || collapse(p.name) === exact)
            score = 1;
        else if (names.some((n) => descriptorKey(n) === key))
            score = 0.95;
        else {
            const nk = descriptorKey(p.name);
            if (key.startsWith(`${nk} `) || (key.length >= 4 && nk.startsWith(key)))
                score = 0.9;
            else
                score = Math.max(...names.map((n) => textSimilarity(key, descriptorKey(n)))) * 0.8;
        }
        if (!best || score > best.score)
            best = { payee: p, score };
    }
    return best && best.score >= minScore ? { payee: best.payee, score: Math.round(best.score * 1000) / 1000 } : null;
};
/**
 * Category suggestion from history: majority category among identical normalised descriptors,
 * else similarity-weighted vote over similar descriptors (≥ 0.6). Confidence 0..1; null when nothing useful.
 */
export const suggestCategory = (payeeRaw, history) => {
    const key = descriptorKey(payeeRaw);
    const vote = (rows, damp) => {
        if (rows.length === 0)
            return null;
        const tally = new Map();
        let total = 0;
        for (const r of rows) {
            tally.set(r.categoryId, (tally.get(r.categoryId) ?? 0) + r.w);
            total += r.w;
        }
        const [categoryId, w] = [...tally.entries()].sort((a, b) => b[1] - a[1])[0];
        const n = rows.length;
        const confidence = Math.round((w / total) * (n / (n + 1)) * damp * 1000) / 1000;
        return confidence >= 0.3 ? { categoryId, confidence } : null;
    };
    const withCat = history.filter((h) => h.categoryId !== null);
    const exact = withCat.filter((h) => descriptorKey(h.payeeRaw) === key).map((h) => ({ categoryId: h.categoryId, w: 1 }));
    if (exact.length > 0)
        return vote(exact, 1);
    const fuzzy = withCat
        .map((h) => ({ categoryId: h.categoryId, w: textSimilarity(key, descriptorKey(h.payeeRaw)) }))
        .filter((r) => r.w >= 0.6);
    return vote(fuzzy, 0.7);
};
//# sourceMappingURL=payees.js.map